"use client";

import { useState, useEffect, useRef } from "react";
import {
  Menu,
  Bell,
  Search,
  HelpCircle,
  Settings,
  User,
  BarChart2,
  TrendingUp,
  PieChart,
  Users,
  CreditCard,
  Layers,
  ChevronDown,
  MessageSquare,
  Calendar,
  RefreshCw,
  LogOut,
  Gift,
  Percent,
  Star,
  Shield,
  Database,
  Link as LinkIcon
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import apiClient from "../../services/api";
import { getUserInitials, getUserDisplayName } from "../../utils/userUtils";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [creditData, setCreditData] = useState({
    totalCredits: 100,
    usedCredits: 0,
    availableCredits: 100,
    percentage: 0
  });
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [readNotifications, setReadNotifications] = useState(new Set());
  // Removed visibleNotifications state - backend returns fixed top 3
  const location = useLocation();
  const { user, logout, isLoading } = useAuth();

  // Fetch credit data
  useEffect(() => {
    const fetchCreditData = async () => {
      try {
        const response = await apiClient.get('/backend/api/credits/balance');
        if (response.data.success) {
          const data = response.data.data;
          const usedPercentage = data.totalCredits > 0 ? Math.round((data.usedCredits / data.totalCredits) * 100) : 0;
          const remainingPercentage = 100 - usedPercentage;
          setCreditData({
            totalCredits: data.totalCredits,
            usedCredits: data.usedCredits,
            availableCredits: data.availableCredits,
            percentage: remainingPercentage
          });
        }
      } catch (error) {
        console.error('Failed to fetch credit data:', error);
        // Keep default values on error
      }
    };

    if (user) {
      fetchCreditData();
    }
  }, [user]);

  // Fetch notifications data from backend and check for new notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;

      setNotificationsLoading(true);
      try {
        // Try multiple endpoints to get notifications
        let response;
        try {
          // First try the notifications endpoint
          response = await apiClient.get('/backend/api/comments/notifications/recent');
        } catch (error) {
          // Fallback to comments endpoint
          response = await apiClient.get('/backend/api/comments');
        }

        if (response.data.success) {
          let recentMessages = [];

          if (response.data.notifications) {
            // From notifications endpoint
            recentMessages = response.data.notifications;
          } else if (response.data.comments) {
            // From comments endpoint - get recent comments (last 24 hours)
            const now = new Date();
            const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

            recentMessages = response.data.comments
              .filter((comment: any) => {
                const commentDate = new Date(comment.created_at || comment.time);
                return commentDate > yesterday;
              })
              .slice(0, 5) // Limit to 5 most recent
              .map((comment: any) => ({
                id: comment._id || comment.id,
                commentId: comment.commentId,
                message: comment.comment || comment.message,
                productName: comment.productName,
                platform: comment.platform,
                time: comment.created_at || comment.time,
                rating: comment.rating
              }));
          }

          // Check if there are new notifications
          if (notifications.length > 0 && recentMessages.length > notifications.length) {
            setHasNewNotifications(true);
            setTimeout(() => setHasNewNotifications(false), 5000);
          }

          setNotifications(recentMessages);

          // Check for new notifications
          if (recentMessages.length > 0) {
            const savedReadNotifications = localStorage.getItem('readNotifications');
            const readIds = savedReadNotifications ? new Set(JSON.parse(savedReadNotifications)) : new Set();
            const hasUnread = recentMessages.some((notif: any) => !readIds.has(notif.id));
            setHasNewNotifications(hasUnread);
          }
        }
      } catch (error) {
        // Keep empty array on error but don't show error to user
        setNotifications([]);
      } finally {
        setNotificationsLoading(false);
      }
    };

    if (user) {
      fetchNotifications();
      // Refresh notifications every 2 minutes
      const interval = setInterval(fetchNotifications, 120000);
      return () => clearInterval(interval);
    }
  }, [user]);





  // Format time for notifications
  const formatNotificationTime = (timeString: string) => {
    try {
      const time = new Date(timeString);
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);

      if (diffInSeconds < 60) {
        return 'Just now';
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes}m ago`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours}h ago`;
      } else {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days}d ago`;
      }
    } catch (error) {
      return 'Unknown';
    }
  };

  // No sample data needed - using real backend notifications

  // Message truncation is handled by the backend (100 chars max)

  // Mark all notifications as read
  const markAllAsRead = () => {
    const allIds = notifications.map(n => n.id);
    setReadNotifications(new Set([...readNotifications, ...allIds]));
    setHasNewNotifications(false);
    // Store in localStorage
    localStorage.setItem('readNotifications', JSON.stringify([...readNotifications, ...allIds]));
  };

  // Handle notification click - redirect to responses page with specific comment
  const handleNotificationClick = (notification: any) => {
    // Mark as read
    const newReadNotifications = new Set([...readNotifications, notification.id]);
    setReadNotifications(newReadNotifications);
    localStorage.setItem('readNotifications', JSON.stringify([...newReadNotifications]));

    setNotificationsOpen(false);
    setHasNewNotifications(false);

    // Use commentId for better navigation to specific comment in responses page
    const highlightParam = notification.commentId || notification.id;
    window.location.href = `/responses?highlight=${highlightParam}`;
  };

  // Load more notifications functionality removed - backend only returns top 3

  // Notification read status is handled inline in the UI

  // Load read notifications from localStorage on mount
  useEffect(() => {
    const savedReadNotifications = localStorage.getItem('readNotifications');
    if (savedReadNotifications) {
      setReadNotifications(new Set(JSON.parse(savedReadNotifications)));
    }
  }, []);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Refs for dropdown containers
  const notificationsRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => {
    // Normalize paths by removing trailing slashes for comparison
    const normalizedPathname = location.pathname.replace(/\/$/, '') || '/';
    const normalizedPath = path.replace(/\/$/, '') || '/';
    return normalizedPathname === normalizedPath;
  };

  // Close all dropdowns when clicking outside
  const handleClickOutside = (event: MouseEvent) => {
    if (
      notificationsRef.current &&
      !notificationsRef.current.contains(event.target as Node) &&
      notificationsOpen
    ) {
      setNotificationsOpen(false);
    }

    if (
      helpRef.current &&
      !helpRef.current.contains(event.target as Node) &&
      helpOpen
    ) {
      setHelpOpen(false);
    }

    if (
      settingsRef.current &&
      !settingsRef.current.contains(event.target as Node) &&
      settingsOpen
    ) {
      setSettingsOpen(false);
    }

    if (
      profileRef.current &&
      !profileRef.current.contains(event.target as Node) &&
      profileOpen
    ) {
      setProfileOpen(false);
    }
  };

  // Add event listener for click outside
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationsOpen, helpOpen, settingsOpen, profileOpen]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">
      {/* Top Navigation */}
      <div className="bg-white dark:bg-gray-900 px-6 py-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-800 shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            className="p-2 rounded-lg top-bar-icon menu-icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu className="h-5 w-5 text-gray-500 dark:text-gray-400 icon-transition" />
          </button>
          <h1 className="text-xl font-bold tracking-tight">
            BIZAMPLY
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative hidden md:block">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="search"
              placeholder="Search..."
              className="search-input pl-10 pr-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm w-48 focus:outline-none border border-gray-200 dark:border-gray-700"
            />
          </div>

          {/* Notifications Dropdown */}
          <div className="relative" ref={notificationsRef}>
            <button
              className={`p-2 rounded-lg top-bar-icon bell-icon relative ${notificationsOpen ? 'bg-gray-100 dark:bg-gray-800' : ''}`}
              onClick={() => {
                setNotificationsOpen(!notificationsOpen);
                setHelpOpen(false);
                setSettingsOpen(false);
                setProfileOpen(false);
                setHasNewNotifications(false);
              }}
            >
              <Bell className="h-5 w-5 text-gray-500 dark:text-gray-400 icon-transition" />
              {(hasNewNotifications || notifications.length > 0) && (
                <span className={`absolute -top-1 -right-1 h-5 w-5 rounded-full ${hasNewNotifications ? 'bg-red-500 notification-pulse' : 'bg-blue-500'} flex items-center justify-center notification-badge`}>
                  {notifications.length > 0 && (
                    <span className="text-xs text-white font-bold">
                      {notifications.length > 9 ? '9+' : notifications.length}
                    </span>
                  )}
                </span>
              )}
            </button>
            <div
              className={`absolute right-0 mt-2 w-80 bg-white dark:bg-gray-900 notification-dropdown rounded-lg shadow-xl py-2 border border-gray-200 dark:border-gray-800 z-50 transition-all duration-100 ease-out transform origin-top-right ${
                notificationsOpen
                  ? 'opacity-100 scale-100 translate-y-0'
                  : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
              }`}
            >
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Notifications</h3>
                  <div className="flex items-center gap-2">
                    <button
                      className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200 top-bar-icon p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Manually refresh notifications
                        if (user) {
                          const fetchNotifications = async () => {
                            setNotificationsLoading(true);
                            try {
                              let response;
                              try {
                                response = await apiClient.get('/backend/api/comments/notifications/recent');
                              } catch (error) {
                                response = await apiClient.get('/backend/api/comments');
                              }

                              if (response.data.success) {
                                let recentMessages = [];

                                if (response.data.notifications) {
                                  recentMessages = response.data.notifications;
                                } else if (response.data.comments) {
                                  const now = new Date();
                                  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

                                  recentMessages = response.data.comments
                                    .filter((comment: any) => {
                                      const commentDate = new Date(comment.created_at || comment.time);
                                      return commentDate > yesterday;
                                    })
                                    .slice(0, 5)
                                    .map((comment: any) => ({
                                      id: comment._id || comment.id,
                                      commentId: comment.commentId,
                                      message: comment.comment || comment.message,
                                      productName: comment.productName,
                                      platform: comment.platform,
                                      time: comment.created_at || comment.time,
                                      rating: comment.rating
                                    }));
                                }

                                setNotifications(recentMessages);
                              }
                            } catch (error) {
                              setNotifications([]);
                            } finally {
                              setNotificationsLoading(false);
                            }
                          };
                          fetchNotifications();
                        }
                      }}
                      title="Refresh notifications"
                    >
                      <RefreshCw className="h-3 w-3" />
                    </button>
                    {/* Debug buttons removed */}
                    <button
                      className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200 top-bar-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAllAsRead();
                      }}
                    >
                      Mark all as read
                    </button>
                  </div>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notificationsLoading ? (
                  <div className="px-4 py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading notifications...</p>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center">
                    <MessageSquare className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No recent messages</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Comments will appear here when customers leave reviews
                    </p>

                  </div>
                ) : (
                  notifications.map((notification, index) => (
                    <div
                      key={notification.id}
                      className="notification-item px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 border-l-2 border-transparent hover:border-blue-500 transition-all duration-200 cursor-pointer"
                      onClick={() => handleNotificationClick(notification)}
                      style={{
                        animation: `fadeInSlide 0.3s ease-out ${index * 0.1}s both`
                      }}
                    >
                      <div className="flex gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {notification.title}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate mt-1">
                            {notification.message}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-gray-500 dark:text-gray-500">
                              {formatNotificationTime(notification.time)}
                            </span>
                            <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full">
                              {notification.platform}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800">
                <button
                  className="w-full text-center text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 py-2 rounded-md transition-all duration-200 transform active:scale-95 font-medium border border-blue-200 dark:border-blue-800"
                  onClick={(e) => {
                    e.stopPropagation();
                    setNotificationsOpen(false);
                    window.location.href = '/responses';
                  }}
                >
                  View All Comments
                </button>
              </div>
            </div>
          </div>

          {/* Help Dropdown */}
          <div className="relative" ref={helpRef}>
            <button
              className={`p-2 rounded-lg top-bar-icon help-icon ${helpOpen ? 'bg-gray-100 dark:bg-gray-800' : ''}`}
              onClick={() => {
                setHelpOpen(!helpOpen);
                setNotificationsOpen(false);
                setSettingsOpen(false);
                setProfileOpen(false);
              }}
            >
              <HelpCircle className="h-5 w-5 text-gray-500 dark:text-gray-400 icon-transition" />
            </button>
            <div
              className={`absolute right-0 mt-2 w-64 bg-white dark:bg-gray-900 rounded-lg shadow-lg py-2 border border-gray-200 dark:border-gray-800 z-50 transition-all duration-150 ease-out transform origin-top-right ${
                helpOpen
                  ? 'opacity-100 scale-100 translate-y-0'
                  : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
              }`}
            >
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center">
                  <HelpCircle className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                  <span>Help Center</span>
                </div>
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                  <span>Contact Support</span>
                </div>
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center">
                  <TrendingUp className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                  <span>What's New</span>
                </div>
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center">
                  <Layers className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                  <span>Documentation</span>
                </div>
              </button>
            </div>
          </div>

          {/* Settings Dropdown */}
          <div className="relative" ref={settingsRef}>
            <button
              className={`p-2 rounded-lg top-bar-icon settings-icon ${settingsOpen ? 'bg-gray-100 dark:bg-gray-800' : ''}`}
              onClick={() => {
                setSettingsOpen(!settingsOpen);
                setNotificationsOpen(false);
                setHelpOpen(false);
                setProfileOpen(false);
              }}
            >
              <Settings className="h-5 w-5 text-gray-500 dark:text-gray-400 icon-transition" />
            </button>
            <div
              className={`absolute right-0 mt-2 w-64 bg-white dark:bg-gray-900 dropdown-menu rounded-lg shadow-xl py-2 border border-gray-200 dark:border-gray-800 z-50 transition-all duration-150 ease-out transform origin-top-right ${
                settingsOpen
                  ? 'opacity-100 scale-100 translate-y-0'
                  : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
              }`}
            >
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800">
                <h3 className="font-medium">Settings</h3>
              </div>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 top-bar-icon"
                onClick={(e) => e.stopPropagation()}
                style={{ animation: `fadeInSlide 0.3s ease-out 0s both` }}
              >
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400 icon-transition" />
                  <span>Account Settings</span>
                </div>
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 top-bar-icon"
                onClick={(e) => e.stopPropagation()}
                style={{ animation: `fadeInSlide 0.3s ease-out 0.1s both` }}
              >
                <div className="flex items-center">
                  <Bell className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400 icon-transition" />
                  <span>Notification Preferences</span>
                </div>
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 top-bar-icon"
                onClick={(e) => e.stopPropagation()}
                style={{ animation: `fadeInSlide 0.3s ease-out 0.2s both` }}
              >
                <div className="flex items-center">
                  <Layers className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400 icon-transition" />
                  <span>Appearance</span>
                </div>
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 top-bar-icon"
                onClick={(e) => e.stopPropagation()}
                style={{ animation: `fadeInSlide 0.3s ease-out 0.3s both` }}
              >
                <div className="flex items-center">
                  <Settings className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400 icon-transition" />
                  <span>Advanced Settings</span>
                </div>
              </button>
            </div>
          </div>

          {/* User Profile Dropdown */}
          <div className="relative" ref={profileRef}>
            <div
              className={`h-8 w-8 rounded-full bg-gray-900 dark:bg-gray-100 flex items-center justify-center text-white dark:text-gray-900 font-medium cursor-pointer profile-icon ${profileOpen ? 'ring-2 ring-blue-300 dark:ring-blue-700' : ''}`}
              onClick={() => {
                setProfileOpen(!profileOpen);
                setNotificationsOpen(false);
                setHelpOpen(false);
                setSettingsOpen(false);
              }}
            >
              {user ? getUserInitials(user) : 'JD'}
            </div>
            <div
              className={`absolute right-0 mt-2 w-64 bg-white dark:bg-gray-900 rounded-lg shadow-lg py-2 border border-gray-200 dark:border-gray-800 z-50 transition-all duration-150 ease-out transform origin-top-right ${
                profileOpen
                  ? 'opacity-100 scale-100 translate-y-0'
                  : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
              }`}
            >
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                <p className="font-medium">{user ? getUserDisplayName(user) : "Your Name"}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email || "user@example.com"}</p>
              </div>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                  <span>Your Profile</span>
                </div>
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center">
                  <Settings className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                  <span>Account Settings</span>
                </div>
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center">
                  <HelpCircle className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400" />
                  <span>Help & Support</span>
                </div>
              </button>
              <div className="border-t border-gray-200 dark:border-gray-800 mt-1"></div>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 group"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLogout();
                }}
              >
                <div className="flex items-center">
                  <LogOut className="h-4 w-4 mr-2 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-200" />
                  <span>Sign out</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar - Mobile Overlay */}
        {sidebarOpen && (
          <div className="md:hidden fixed inset-0 bg-black/50 z-20" onClick={() => setSidebarOpen(false)}></div>
        )}

        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'} fixed md:sticky top-[57px] w-64 h-[calc(100vh-57px)] bg-white dark:bg-gray-900 p-4 border-r border-gray-200 dark:border-gray-800 z-30 transition-transform duration-300 ease-in-out overflow-y-auto`}>
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-gray-900 dark:bg-gray-100 flex items-center justify-center text-white dark:text-gray-900 font-bold shadow-md">
                B
              </div>
              <div>
                <div className="font-semibold">Bizamply</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Analytics Platform</div>
              </div>
            </div>

            <div className="space-y-1">
              <Link to="/dashboard" className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${isActive('/dashboard') ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'} transition-all duration-200 transform active:scale-95 ${isActive('/dashboard') ? 'shadow-sm' : ''}`}>
                <BarChart2 className={`h-5 w-5 ${isActive('/dashboard') ? 'text-white dark:text-gray-900' : 'text-gray-500 dark:text-gray-400'}`} />
                <span>Dashboard</span>
              </Link>

              <Link to="/analytics" className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${isActive('/analytics') ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'} transition-all duration-200 transform active:scale-95 ${isActive('/analytics') ? 'shadow-sm' : ''}`}>
                <TrendingUp className={`h-5 w-5 ${isActive('/analytics') ? 'text-white dark:text-gray-900' : 'text-gray-500 dark:text-gray-400'}`} />
                <span>Analytics</span>
              </Link>

              <Link to="/loyalty-manager" className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${isActive('/loyalty-manager') ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'} transition-all duration-200 transform active:scale-95 ${isActive('/loyalty-manager') ? 'shadow-sm' : ''}`}>
                <Gift className={`h-5 w-5 ${isActive('/loyalty-manager') ? 'text-white dark:text-gray-900' : 'text-gray-500 dark:text-gray-400'}`} />
                <span>Loyalty Manager</span>
              </Link>

              <Link to="/discount-manager" className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${isActive('/discount-manager') ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'} transition-all duration-200 transform active:scale-95 ${isActive('/discount-manager') ? 'shadow-sm' : ''}`}>
                <Percent className={`h-5 w-5 ${isActive('/discount-manager') ? 'text-white dark:text-gray-900' : 'text-gray-500 dark:text-gray-400'}`} />
                <span>Discount Manager</span>
              </Link>

              <Link to="/responses" className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${isActive('/responses') ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'} transition-all duration-200 transform active:scale-95 ${isActive('/responses') ? 'shadow-sm' : ''}`}>
                <MessageSquare className={`h-5 w-5 ${isActive('/responses') ? 'text-white dark:text-gray-900' : 'text-gray-500 dark:text-gray-400'}`} />
                <span>Responses</span>
              </Link>

              {/* Admin-only Customers route */}
              {user?.email === 'admin@bizamply.com' && (
                <Link to="/customers" className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${isActive('/customers') ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'} transition-all duration-200 transform active:scale-95 ${isActive('/customers') ? 'shadow-sm' : ''}`}>
                  <Users className={`h-5 w-5 ${isActive('/customers') ? 'text-white dark:text-gray-900' : 'text-gray-500 dark:text-gray-400'}`} />
                  <span>Customers</span>
                  <span className="ml-auto text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded-full">Admin</span>
                </Link>
              )}

              <Link to="/transactions" className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${isActive('/transactions') ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'} transition-all duration-200 transform active:scale-95 ${isActive('/transactions') ? 'shadow-sm' : ''}`}>
                <CreditCard className={`h-5 w-5 ${isActive('/transactions') ? 'text-white dark:text-gray-900' : 'text-gray-500 dark:text-gray-400'}`} />
                <span>Transactions</span>
              </Link>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              ACCOUNT
            </h3>
            <div className="space-y-1">
              <Link to="/profile" className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${isActive('/profile') ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'} transition-all duration-200 transform active:scale-95 ${isActive('/profile') ? 'shadow-sm' : ''}`}>
                <User className={`h-5 w-5 ${isActive('/profile') ? 'text-white dark:text-gray-900' : 'text-gray-500 dark:text-gray-400'}`} />
                <span>Profile</span>
              </Link>

              <Link to="/connections" className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${isActive('/connections') ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'} transition-all duration-200 transform active:scale-95 ${isActive('/connections') ? 'shadow-sm' : ''}`}>
                <LinkIcon className={`h-5 w-5 ${isActive('/connections') ? 'text-white dark:text-gray-900' : 'text-gray-500 dark:text-gray-400'}`} />
                <span>Connections</span>
              </Link>

              <Link to="/settings" className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${isActive('/settings') ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'} transition-all duration-200 transform active:scale-95 ${isActive('/settings') ? 'shadow-sm' : ''}`}>
                <Settings className={`h-5 w-5 ${isActive('/settings') ? 'text-white dark:text-gray-900' : 'text-gray-500 dark:text-gray-400'}`} />
                <span>Settings</span>
              </Link>
            </div>
          </div>

          <div className="mt-auto pt-6">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium">Credits</h4>
                <span className="text-xs text-gray-500 dark:text-gray-400">{creditData.percentage}% remaining</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-600 dark:bg-green-400 rounded-full transition-all duration-300"
                  style={{ width: `${Math.max(2, creditData.percentage)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {creditData.availableCredits} of {creditData.totalCredits} credits remaining
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto h-[calc(100vh-57px)]">
          <main className="p-4 md:p-6 max-w-7xl mx-auto">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
